import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Tooltip,
  TextField,
} from "@mui/material";
import { ReceiptLong as ReceiptLongIcon } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import formOptions from "../../config/formOptions.json";
import LabelSelector from "../../components/LabelSelector";
import DateInput from "../../components/DateInput";
import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";
import { graphqlEndpoint } from "../../config";
import { parseUKDate } from "../../utils/dateUtiles";

import { READ_PROJECT, READ_PROJECTS_FILTER } from "../../graph/projects/queries";
import { DELETE_TASK } from "../../graph/tasks/mutations";
import { UPDATE_PROJECT } from "../../graph/projects/mutations";

const client = new GraphQLClient(graphqlEndpoint);
const getProjectCostsPath = (pid) => `/manageProjects/${pid}/costs`;

const allowedLabels =
  (formOptions?.labelOptions || []).map((l) => l.value) || [];

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.string().nullable(),
  isSOPTemplate: yup.boolean(),
  labels: yup.array().of(yup.string().oneOf(allowedLabels, "Invalid label")),
  sourceProjectId: yup.string().nullable(),
  dueDate: yup.string().nullable(),
  status: yup.string().required("Status is required"),
});

const ProjectEditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();

  const [initialValues, setInitialValues] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sourceProjects, setSourceProjects] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const redirectPath = location.state?.redirectPath || "/manageProjects";

  useEffect(() => {
    const fetchProject = async () => {
      if (!tenantId) return;

      try {
        const [{ readSingleProjectById }, { readProjectsFilter }] = await Promise.all([
          client.request(READ_PROJECT, { id }),
          client.request(READ_PROJECTS_FILTER, {
            filter: {
              tenantId,
              isSOPTemplate: true,
            },
          }),
        ]);

        const project = readSingleProjectById;

        if (location.state?.refresh) {
          navigate(location.pathname, { replace: true, state: {} });
        }

        if (!project) {
          setSnackbar({
            open: true,
            severity: "error",
            message: "Project not found",
          });
          navigate(redirectPath);
          return;
        }

        if (project.tenantId !== tenantId) {
          setSnackbar({
            open: true,
            severity: "error",
            message: "This project does not belong to the selected tenant",
          });
          navigate(redirectPath);
          return;
        }

        setSourceProjects((readProjectsFilter || []).filter((p) => p.id !== id));

        setInitialValues({
          id: project.id,
          title: project.title || "",
          description: project.description || "",
          labels: project.labels || [],
          dueDate: project.dueDate || "",
          status: project.status || "ACTIVE",
          isSOPTemplate: project.isSOPTemplate ?? false,
          sourceProjectId: project.sourceProjectId || "",
          archivedAt: project.archivedAt || null,
        });

        setTasks(project.tasks || []);
      } catch (error) {
        console.error("Error fetching project:", error);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load project",
        });
      }
    };

    fetchProject();
  }, [id, tenantId, navigate, redirectPath, setSnackbar, location.state?.refresh]);

  const handleFormSubmit = async (values) => {
    try {
      await client.request(UPDATE_PROJECT, {
        input: {
          id: values.id,
          title: values.title.trim(),
          description: values.description?.trim() || null,
          isSOPTemplate: values.isSOPTemplate,
          labels: values.labels || [],
          sourceProjectId: values.sourceProjectId || null,
          dueDate: values.dueDate ? parseUKDate(values.dueDate) : null,
          status: values.status,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Project updated successfully",
      });

      navigate(redirectPath, { state: { refresh: true } });
    } catch (error) {
      console.error("Error updating project:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update project",
      });
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask?.id) return;

    try {
      await client.request(DELETE_TASK, { id: selectedTask.id });
      setTasks((prev) => prev.filter((task) => task.id !== selectedTask.id));
      setDeleteModalOpen(false);
      setSelectedTask(null);

      setSnackbar({
        open: true,
        severity: "success",
        message: "Task deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete task",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="EDIT PROJECT"
          subtitle="Select a tenant before editing a project"
        />
        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Header
        title="EDIT PROJECT"
        subtitle={`Edit ${initialValues.title}${tenantName ? ` for ${tenantName}` : ""}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="20px" gridTemplateColumns="repeat(4, minmax(0, 1fr))">
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) => setFieldValue("description", nextValue)}
                label="Description"
                minRows={14}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {(formOptions.projectStatusOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DateInput
                label="Due Date"
                name="dueDate"
                value={values.dueDate}
                onChange={setFieldValue}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                error={errors.dueDate}
                touched={touched.dueDate}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Source SOP</InputLabel>
                <Select
                  name="sourceProjectId"
                  value={values.sourceProjectId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {sourceProjects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Type"
                value={values.isSOPTemplate ? "SOP Template" : "Project"}
                InputProps={{ readOnly: true }}
                sx={{ gridColumn: "span 2" }}
              />

              <LabelSelector
                selectedLabels={values.labels}
                setFieldValue={setFieldValue}
                error={errors.labels}
                touched={touched.labels}
              />

              {values.archivedAt && (
                <Box sx={{ gridColumn: "span 4" }}>
                  <Typography variant="body2" color="warning.main">
                    This project is archived and should generally not be edited.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box display="flex" justifyContent="flex-end" alignItems="center" gap="10px" mt="20px">
              {!values.isSOPTemplate && (
                <Tooltip title="View costs for this project">
                  <span>
                    <ActionButton
                      tone="teal"
                      size="small"
                      startIcon={<ReceiptLongIcon sx={{ color: "inherit" }} />}
                      onClick={() => navigate(getProjectCostsPath(id))}
                      sx={{ color: "#fff" }}
                    >
                      View Project Costs
                    </ActionButton>
                  </span>
                </Tooltip>
              )}

              <ActionButton type="submit" tone="success">
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>

      <Box mt="40px">
        <Header title="RELATED TASKS" subtitle="Tasks linked to this project" />
        <ActionButton
        tone="success"
        size="small"
        onClick={() =>
          navigate("/createTask", {
            state: {
              projectId: id,
              redirectPath: initialValues.isSOPTemplate
                ? `/sops/edit/${id}`
                : `/projects/edit/${id}`,
            },
          })
        }
      >
        + Task
      </ActionButton>

        <DataGrid
          autoHeight
          rows={tasks}
          columns={[
            { field: "title", headerName: "Title", flex: 1.3 },
            { field: "status", headerName: "Status", flex: 0.8 },
            { field: "sequence", headerName: "Sequence", flex: 0.8 },
            { field: "dueDate", headerName: "Due Date", flex: 0.9 },
            {
              field: "actions",
              headerName: "Actions",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Box display="flex" gap="10px">
                  <ActionButton
                    size="small"
                    tone="teal"
                    onClick={() =>
                      navigate(`/tasks/edit/${row.id}`, {
                        state: { redirectPath: `/projects/edit/${id}` },
                      })
                    }
                  >
                    View / Edit
                  </ActionButton>
                  <ActionButton
                    size="small"
                    tone="danger"
                    onClick={() => {
                      setSelectedTask(row);
                      setDeleteModalOpen(true);
                    }}
                  >
                    Delete
                  </ActionButton>
                </Box>
              ),
            },
          ]}
        />
      </Box>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Box
          p={4}
          bgcolor="background.paper"
          borderRadius="12px"
          mx="auto"
          my="20vh"
          width="400px"
          boxShadow={24}
        >
          <Typography variant="h6">Delete Task</Typography>
          <Typography mb={2}>Are you sure you want to delete this task?</Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDeleteTask}>
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProjectEditForm;